import React from 'react'

interface PaginationProps {
	currentPage?: number
	totalPages?: number
	hasPreviousPage?: boolean
	hasNextPage?: boolean
	pageSize?: number
	onPageChange: (pageNumber: number) => void
	onPageSizeChange?: (pageSize: number) => void
}

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100]

const Pagination: React.FC<PaginationProps> = ({ currentPage = 1, totalPages = 1, hasPreviousPage = false, hasNextPage = false, pageSize = 20, onPageChange, onPageSizeChange }) => {
	const normalizedTotalPages = Math.max(1, totalPages)
	const normalizedCurrentPage = Math.min(Math.max(1, currentPage), normalizedTotalPages)

	const handlePageClick = (pageNumber: number) => {
		if (normalizedCurrentPage !== pageNumber) {
			onPageChange(pageNumber)
		}
	}

	const handlePrevious = () => {
		if (hasPreviousPage && normalizedCurrentPage > 1) {
			onPageChange(normalizedCurrentPage - 1)
		}
	}

	const handleNext = () => {
		if (hasNextPage && normalizedCurrentPage < normalizedTotalPages) {
			onPageChange(normalizedCurrentPage + 1)
		}
	}

	const getVisiblePages = () => {
		if (normalizedTotalPages <= 5) {
			return Array.from({ length: normalizedTotalPages }, (_, index) => index + 1)
		}

		const pages = new Set([1, normalizedTotalPages, normalizedCurrentPage])
		if (normalizedCurrentPage > 2) pages.add(normalizedCurrentPage - 1)
		if (normalizedCurrentPage < normalizedTotalPages - 1) pages.add(normalizedCurrentPage + 1)

		return Array.from(pages).sort((a, b) => a - b)
	}

	const visiblePages = getVisiblePages()

	return (
		<div className="data-grid-footer">
			<div className="gridjs-pagination">
				<div className="data-grid-pagination-controls">
					<label className="data-grid-page-size">
						<span>Rows</span>
						<select value={pageSize} onChange={(event) => onPageSizeChange?.(Number(event.target.value))} aria-label="Rows per page">
							{PAGE_SIZE_OPTIONS.map((option) => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
						</select>
					</label>

					<div className="data-grid-pagination-summary">
						Page <strong>{normalizedCurrentPage}</strong> of <strong>{normalizedTotalPages}</strong>
					</div>

					<div className="gridjs-pages">
						<button className="gridjs-page-nav" role="button" title="First" aria-label="First page" onClick={() => handlePageClick(1)} disabled={normalizedCurrentPage === 1}>
							<i className="ri-skip-left-line" aria-hidden />
						</button>

						<button className="gridjs-page-nav" role="button" title="Previous" aria-label="Previous page" onClick={handlePrevious} disabled={!hasPreviousPage}>
							<i className="ri-arrow-left-s-line" aria-hidden />
						</button>

						{visiblePages.map((page, index) => (
							<React.Fragment key={page}>
								{index > 0 && page - visiblePages[index - 1] > 1 && <span className="gridjs-page-ellipsis">...</span>}
								<button role="button" className={normalizedCurrentPage === page ? 'gridjs-currentPage' : ''} title={`Page ${page}`} aria-label={`Page ${page}`} onClick={() => handlePageClick(page)}>
									{page}
								</button>
							</React.Fragment>
						))}

						<button className="gridjs-page-nav" role="button" title="Next" aria-label="Next page" onClick={handleNext} disabled={!hasNextPage}>
							<i className="ri-arrow-right-s-line" aria-hidden />
						</button>

						<button className="gridjs-page-nav" role="button" title="Last" aria-label="Last page" onClick={() => handlePageClick(normalizedTotalPages)} disabled={normalizedCurrentPage === normalizedTotalPages}>
							<i className="ri-skip-right-line" aria-hidden />
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Pagination
